<script setup>
import { ref, computed } from 'vue';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { useVuelidate } from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import NextButton from 'dashboard/components-next/button/Button.vue';

const { t } = useI18n();
const router = useRouter();
const store = useStore();

// State
const inboxName = ref('');
const vendorUid = ref('');
const apiToken = ref('');
const phoneNumberId = ref('');
const isCreating = ref(false);
const createdInbox = ref(null);
const showWebhookScreen = ref(false);
const copied = ref(false);

// Validation
const rules = {
  inboxName: { required },
  vendorUid: { required },
  apiToken: { required },
};

const v$ = useVuelidate(rules, { inboxName, vendorUid, apiToken });

// Computed
const uiFlags = computed(() => store.getters['inboxes/getUIFlags']);

const webhookUrl = computed(() => {
  return createdInbox.value?.webhook_url || '';
});

// Methods
const createChannel = async () => {
  v$.value.$touch();
  if (v$.value.$invalid) {
    return;
  }

  try {
    isCreating.value = true;

    const response = await store.dispatch('inboxes/createChannel', {
      name: inboxName.value,
      channel: {
        type: 'whatsapp',
        provider: 'whats_gateway',
        provider_config: {
          vendor_uid: vendorUid.value,
          api_token: apiToken.value,
          phone_number_id: phoneNumberId.value || undefined,
        },
      },
    });

    createdInbox.value = response;
    showWebhookScreen.value = true;
  } catch (error) {
    const errorMessage =
      error.message ||
      t('INBOX_MGMT.ADD.WHATSAPP.WHATS_GATEWAY.ERROR_MESSAGE');
    useAlert(errorMessage);
  } finally {
    isCreating.value = false;
  }
};

const copyWebhookUrl = async () => {
  try {
    await navigator.clipboard.writeText(webhookUrl.value);
    copied.value = true;
    useAlert('Webhook URL copied!');
    setTimeout(() => {
      copied.value = false;
    }, 2000);
  } catch {
    // Fallback for older browsers
    const textArea = document.createElement('textarea');
    textArea.value = webhookUrl.value;
    document.body.appendChild(textArea);
    textArea.select();
    document.execCommand('copy');
    document.body.removeChild(textArea);
    copied.value = true;
    setTimeout(() => {
      copied.value = false;
    }, 2000);
  }
};

const goToInboxSettings = () => {
  router.push({
    name: 'settings_inboxes_add_agents',
    params: {
      page: 'new',
      inbox_id: createdInbox.value.id,
    },
  });
};
</script>

<template>
  <div class="overflow-auto col-span-6 p-6 w-full h-full">
    <!-- Form Screen -->
    <form
      v-if="!showWebhookScreen"
      class="flex flex-col gap-6"
      @submit.prevent="createChannel"
    >
      <div class="mb-4">
        <h1 class="mb-2 text-xl font-medium text-slate-12">
          {{ $t('INBOX_MGMT.ADD.WHATSAPP.WHATS_GATEWAY.TITLE') }}
        </h1>
        <p class="text-sm leading-relaxed text-slate-11">
          {{ $t('INBOX_MGMT.ADD.WHATSAPP.WHATS_GATEWAY.SUBTITLE') }}
        </p>
      </div>

      <!-- Inbox Name -->
      <div class="flex flex-col gap-2">
        <label
          for="wg-inbox-name"
          class="text-sm font-medium text-slate-12"
          :class="{ 'text-red-600': v$.inboxName.$error }"
        >
          {{ $t('INBOX_MGMT.ADD.WHATSAPP.WHATS_GATEWAY.INBOX_NAME.LABEL') }}
        </label>
        <input
          id="wg-inbox-name"
          v-model="inboxName"
          type="text"
          class="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          :class="{
            'border-slate-300': !v$.inboxName.$error,
            'border-red-500': v$.inboxName.$error,
          }"
          :placeholder="
            $t(
              'INBOX_MGMT.ADD.WHATSAPP.WHATS_GATEWAY.INBOX_NAME.PLACEHOLDER'
            )
          "
          @blur="v$.inboxName.$touch"
        />
        <span v-if="v$.inboxName.$error" class="text-sm text-red-600">
          {{ $t('INBOX_MGMT.ADD.WHATSAPP.WHATS_GATEWAY.INBOX_NAME.ERROR') }}
        </span>
      </div>

      <!-- Vendor UID -->
      <div class="flex flex-col gap-2">
        <label
          for="wg-vendor-uid"
          class="text-sm font-medium text-slate-12"
          :class="{ 'text-red-600': v$.vendorUid.$error }"
        >
          {{ $t('INBOX_MGMT.ADD.WHATSAPP.WHATS_GATEWAY.VENDOR_UID.LABEL') }}
        </label>
        <input
          id="wg-vendor-uid"
          v-model="vendorUid"
          type="text"
          class="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          :class="{
            'border-slate-300': !v$.vendorUid.$error,
            'border-red-500': v$.vendorUid.$error,
          }"
          :placeholder="
            $t(
              'INBOX_MGMT.ADD.WHATSAPP.WHATS_GATEWAY.VENDOR_UID.PLACEHOLDER'
            )
          "
          @blur="v$.vendorUid.$touch"
        />
        <p class="text-xs text-slate-11">
          {{ $t('INBOX_MGMT.ADD.WHATSAPP.WHATS_GATEWAY.VENDOR_UID.HELP') }}
        </p>
        <span v-if="v$.vendorUid.$error" class="text-sm text-red-600">
          {{ $t('INBOX_MGMT.ADD.WHATSAPP.WHATS_GATEWAY.VENDOR_UID.ERROR') }}
        </span>
      </div>

      <!-- API Token -->
      <div class="flex flex-col gap-2">
        <label
          for="wg-api-token"
          class="text-sm font-medium text-slate-12"
          :class="{ 'text-red-600': v$.apiToken.$error }"
        >
          {{ $t('INBOX_MGMT.ADD.WHATSAPP.WHATS_GATEWAY.API_TOKEN.LABEL') }}
        </label>
        <input
          id="wg-api-token"
          v-model="apiToken"
          type="password"
          class="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          :class="{
            'border-slate-300': !v$.apiToken.$error,
            'border-red-500': v$.apiToken.$error,
          }"
          :placeholder="
            $t(
              'INBOX_MGMT.ADD.WHATSAPP.WHATS_GATEWAY.API_TOKEN.PLACEHOLDER'
            )
          "
          @blur="v$.apiToken.$touch"
        />
        <p class="text-xs text-slate-11">
          {{ $t('INBOX_MGMT.ADD.WHATSAPP.WHATS_GATEWAY.API_TOKEN.HELP') }}
        </p>
        <span v-if="v$.apiToken.$error" class="text-sm text-red-600">
          {{ $t('INBOX_MGMT.ADD.WHATSAPP.WHATS_GATEWAY.API_TOKEN.ERROR') }}
        </span>
      </div>

      <!-- Phone Number ID (optional) -->
      <div class="flex flex-col gap-2">
        <label for="wg-phone-number-id" class="text-sm font-medium text-slate-12">
          {{
            $t(
              'INBOX_MGMT.ADD.WHATSAPP.WHATS_GATEWAY.PHONE_NUMBER_ID.LABEL'
            )
          }}
        </label>
        <input
          id="wg-phone-number-id"
          v-model="phoneNumberId"
          type="text"
          class="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          :placeholder="
            $t(
              'INBOX_MGMT.ADD.WHATSAPP.WHATS_GATEWAY.PHONE_NUMBER_ID.PLACEHOLDER'
            )
          "
        />
        <p class="text-xs text-slate-11">
          {{
            $t(
              'INBOX_MGMT.ADD.WHATSAPP.WHATS_GATEWAY.PHONE_NUMBER_ID.HELP'
            )
          }}
        </p>
      </div>

      <!-- Submit Button -->
      <div class="flex gap-3 mt-4">
        <NextButton
          type="submit"
          :is-loading="isCreating || uiFlags.isCreating"
          :label="$t('INBOX_MGMT.ADD.WHATSAPP.WHATS_GATEWAY.CREATE_BUTTON')"
          solid
          blue
        />
      </div>
    </form>

    <!-- Webhook URL Screen (after inbox creation) -->
    <div v-else class="flex flex-col gap-6">
      <div class="mb-4">
        <h1 class="mb-2 text-xl font-medium text-slate-12">
          {{
            $t(
              'INBOX_MGMT.ADD.WHATSAPP.WHATS_GATEWAY.WEBHOOK_SECTION.TITLE'
            )
          }}
        </h1>
        <p class="text-sm leading-relaxed text-green-600">
          {{ $t('INBOX_MGMT.ADD.WHATSAPP.WHATS_GATEWAY.WEBHOOK_SECTION.INBOX_CREATED') }}
        </p>
      </div>

      <!-- Webhook URL -->
      <div class="p-6 bg-blue-50 rounded-lg border border-blue-200">
        <p class="text-sm text-blue-900 mb-4">
          {{
            $t(
              'INBOX_MGMT.ADD.WHATSAPP.WHATS_GATEWAY.WEBHOOK_SECTION.DESCRIPTION'
            )
          }}
        </p>

        <div
          class="flex items-center gap-2 p-3 bg-white rounded-lg border border-blue-300"
        >
          <code class="flex-1 text-sm text-blue-800 break-all">
            {{ webhookUrl }}
          </code>
          <button
            class="px-3 py-1.5 text-sm font-medium rounded-md flex-shrink-0"
            :class="
              copied
                ? 'bg-green-100 text-green-700'
                : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
            "
            @click="copyWebhookUrl"
          >
            {{
              copied
                ? $t(
                    'INBOX_MGMT.ADD.WHATSAPP.WHATS_GATEWAY.WEBHOOK_SECTION.COPIED'
                  )
                : $t(
                    'INBOX_MGMT.ADD.WHATSAPP.WHATS_GATEWAY.WEBHOOK_SECTION.COPY_BUTTON'
                  )
            }}
          </button>
        </div>

        <p class="mt-4 text-sm text-blue-800">
          {{
            $t(
              'INBOX_MGMT.ADD.WHATSAPP.WHATS_GATEWAY.WEBHOOK_SECTION.NEXT_STEPS'
            )
          }}
        </p>
      </div>

      <!-- Action Buttons -->
      <div class="flex gap-3 mt-6 pt-6 border-t border-slate-200">
        <NextButton
          :label="$t('INBOX_MGMT.ADD.WHATSAPP.WHATS_GATEWAY.FINISH_BUTTON')"
          solid
          blue
          @click="goToInboxSettings"
        />
      </div>
    </div>
  </div>
</template>
